/**
 * Frame Sorting Utilities
 * 
 * Provides functions for sorting frames by category to improve user experience
 * when selecting frames. Frames are grouped by category and sorted alphabetically
 * within each category.
 * 
 * Requirements: 9.5
 */

import type { LocalFrame } from '../../../lib/db/schema';

/**
 * Sort frames by category ID, then by name within each category
 * 
 * @param frames - Array of frames to sort
 * @returns Sorted array of frames grouped by category
 */
export function sortFramesByCategory(frames: LocalFrame[]): LocalFrame[] {
  return [...frames].sort((a, b) => {
    // Sort by category first
    if (a.categoryId !== b.categoryId) {
      return a.categoryId.localeCompare(b.categoryId);
    }
    // Then by name within the same category
    return a.name.localeCompare(b.name);
  });
}

/**
 * Group frames by category ID
 * 
 * @param frames - Array of frames to group
 * @returns Map of category ID to frames in that category
 */
export function groupFramesByCategory(frames: LocalFrame[]): Map<string, LocalFrame[]> {
  const groups = new Map<string, LocalFrame[]>();
  
  for (const frame of frames) {
    const existing = groups.get(frame.categoryId) || [];
    groups.set(frame.categoryId, [...existing, frame]);
  }
  
  return groups;
}

/**
 * Check if frames contain multiple categories
 * 
 * @param frames - Array of frames to check
 * @returns True if frames contain more than one category
 */
export function hasMultipleCategories(frames: LocalFrame[]): boolean {
  const categories = new Set(frames.map(f => f.categoryId));
  return categories.size > 1;
}
